import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Label } from '@/components/ui/label'
import { blink } from '@/lib/blink'
import { toast } from 'sonner'
import { 
  Mail, 
  Calendar, 
  Send, 
  Search, 
  FileText, 
  Trash2, 
  FolderOpen,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Eye
} from 'lucide-react'

interface TestResult {
  id: string
  timestamp: string
  operation: string
  status: 'success' | 'error' | 'pending'
  duration: number
  request: any
  response: any
  error?: string
}

export default function LiveGraphAPITest() {
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [testResults, setTestResults] = useState<TestResult[]>([])
  
  // Email test inputs
  const [searchQuery, setSearchQuery] = useState('from:nicole')
  const [emailTo, setEmailTo] = useState('nicole@example.com')
  const [emailSubject, setEmailSubject] = useState('Test Email from AI Agent')
  const [emailBody, setEmailBody] = useState('This is a live test email sent via Microsoft Graph API.')
  const [draftSubject, setDraftSubject] = useState('Draft: AI Generated Email')
  const [draftBody, setDraftBody] = useState('This draft was created by the AI agent.')
  const [messageIdToMove, setMessageIdToMove] = useState('')
  const [targetFolder, setTargetFolder] = useState('Archive')
  const [messageIdToDelete, setMessageIdToDelete] = useState('')
  
  // Calendar test inputs
  const [eventTitle, setEventTitle] = useState('AI Agent Test Meeting')
  const [eventStart, setEventStart] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 16))
  const [eventEnd, setEventEnd] = useState(new Date(Date.now() + 90000000).toISOString().slice(0, 16))
  const [eventAttendees, setEventAttendees] = useState('nicole@example.com')
  const [calendarSearchQuery, setCalendarSearchQuery] = useState('meeting')

  // Load user on mount
  useState(() => {
    blink.auth.onAuthStateChanged((state) => {
      setUser(state.user)
    })
  })

  const addTestResult = (result: Omit<TestResult, 'id' | 'timestamp'>) => {
    const newResult: TestResult = {
      ...result,
      id: `test-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString()
    }
    setTestResults(prev => [newResult, ...prev])
    return newResult
  }

  const updateTestResult = (id: string, updates: Partial<TestResult>) => {
    setTestResults(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r))
  }

  // Helper to get valid access token
  const getValidAccessToken = async () => {
    const accounts = await blink.db.emailAccounts.list({
      where: { userId: user.id, provider: 'outlook' },
      limit: 1
    })
    
    if (!accounts || accounts.length === 0) {
      throw new Error('No Outlook account connected')
    }
    
    const account = accounts[0]
    const expiresAt = new Date(account.tokenExpiresAt)
    
    // Check if token needs refresh
    if (expiresAt <= new Date()) {
      const refreshResponse = await fetch(`https://${import.meta.env.VITE_BLINK_FUNCTION_URL}/oauth-refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          accountId: account.id,
          refreshToken: account.refreshToken
        })
      })
      
      const refreshData = await refreshResponse.json()
      if (!refreshData.success) {
        throw new Error('Failed to refresh token')
      }
      
      return refreshData.accessToken
    }
    
    return account.accessToken
  }

  // Email API Tests
  const testSendEmail = async () => {
    const testId = addTestResult({
      operation: 'Send Email',
      status: 'pending',
      duration: 0,
      request: { to: emailTo, subject: emailSubject, body: emailBody },
      response: null
    }).id

    const startTime = Date.now()
    
    try {
      const accessToken = await getValidAccessToken()
      
      const response = await fetch('https://graph.microsoft.com/v1.0/me/sendMail', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: {
            subject: emailSubject,
            body: { contentType: 'Text', content: emailBody },
            toRecipients: [{ emailAddress: { address: emailTo } }]
          }
        })
      })

      const duration = Date.now() - startTime
      
      if (response.ok) {
        updateTestResult(testId, {
          status: 'success',
          duration,
          response: { statusCode: response.status, message: 'Email sent successfully' }
        })
        toast.success('Email sent successfully!')
      } else {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Failed to send email')
      }
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message,
        response: null
      })
      toast.error(`Send failed: ${error.message}`)
    }
  }

  const testSearchEmails = async () => {
    const testId = addTestResult({
      operation: 'Search Emails',
      status: 'pending',
      duration: 0,
      request: { query: searchQuery },
      response: null
    }).id

    const startTime = Date.now()
    
    try {
      const accessToken = await getValidAccessToken()
      
      const response = await fetch(
        `https://graph.microsoft.com/v1.0/me/messages?$search="${searchQuery}"&$top=10&$select=subject,from,receivedDateTime,bodyPreview`,
        {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        }
      )

      const duration = Date.now() - startTime
      const data = await response.json()
      
      if (response.ok) {
        updateTestResult(testId, {
          status: 'success',
          duration,
          response: { count: data.value?.length || 0, emails: data.value }
        })
        toast.success(`Found ${data.value?.length || 0} emails`)
      } else {
        throw new Error(data.error?.message || 'Search failed')
      }
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message,
        response: null
      })
      toast.error(`Search failed: ${error.message}`)
    }
  }

  const testCreateDraft = async () => {
    const testId = addTestResult({
      operation: 'Create Draft',
      status: 'pending',
      duration: 0,
      request: { subject: draftSubject, body: draftBody },
      response: null
    }).id

    const startTime = Date.now()
    
    try {
      const accessToken = await getValidAccessToken()
      
      const response = await fetch('https://graph.microsoft.com/v1.0/me/messages', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: draftSubject,
          body: { contentType: 'Text', content: draftBody },
          toRecipients: [{ emailAddress: { address: emailTo } }]
        })
      })

      const duration = Date.now() - startTime
      const data = await response.json()
      
      if (response.ok) {
        updateTestResult(testId, {
          status: 'success',
          duration,
          response: { draftId: data.id, subject: data.subject }
        })
        toast.success('Draft created successfully!')
      } else {
        throw new Error(data.error?.message || 'Failed to create draft')
      }
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message,
        response: null
      })
      toast.error(`Draft creation failed: ${error.message}`)
    }
  }

  const testMoveEmail = async () => {
    if (!messageIdToMove) {
      toast.error('Please enter a message ID')
      return
    }

    const testId = addTestResult({
      operation: 'Move Email',
      status: 'pending',
      duration: 0,
      request: { messageId: messageIdToMove, targetFolder },
      response: null
    }).id

    const startTime = Date.now()
    
    try {
      const accessToken = await getValidAccessToken()
      
      const response = await fetch(
        `https://graph.microsoft.com/v1.0/me/messages/${messageIdToMove}/move`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ destinationId: targetFolder })
        }
      )

      const duration = Date.now() - startTime
      const data = await response.json()
      
      if (response.ok) {
        updateTestResult(testId, {
          status: 'success',
          duration,
          response: { messageId: data.id, parentFolderId: data.parentFolderId }
        })
        toast.success('Email moved successfully!')
      } else {
        throw new Error(data.error?.message || 'Failed to move email')
      }
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message,
        response: null
      })
      toast.error(`Move failed: ${error.message}`)
    }
  }

  const testDeleteEmail = async () => {
    if (!messageIdToDelete) {
      toast.error('Please enter a message ID')
      return
    }

    const testId = addTestResult({
      operation: 'Delete Email',
      status: 'pending',
      duration: 0,
      request: { messageId: messageIdToDelete },
      response: null
    }).id

    const startTime = Date.now()
    
    try {
      const accessToken = await getValidAccessToken()
      
      const response = await fetch(
        `https://graph.microsoft.com/v1.0/me/messages/${messageIdToDelete}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${accessToken}` }
        }
      )

      const duration = Date.now() - startTime
      
      if (response.ok) {
        updateTestResult(testId, {
          status: 'success',
          duration,
          response: { statusCode: response.status, message: 'Email deleted successfully' }
        })
        toast.success('Email deleted successfully!')
      } else {
        const data = await response.json()
        throw new Error(data.error?.message || 'Failed to delete email')
      }
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message,
        response: null
      })
      toast.error(`Delete failed: ${error.message}`)
    }
  }

  // Calendar API Tests
  const testCreateCalendarEvent = async () => {
    const testId = addTestResult({
      operation: 'Create Calendar Event',
      status: 'pending',
      duration: 0,
      request: { title: eventTitle, start: eventStart, end: eventEnd, attendees: eventAttendees },
      response: null
    }).id

    const startTime = Date.now()
    
    try {
      const accessToken = await getValidAccessToken()
      
      const attendeesList = eventAttendees
        .split(',')
        .map(email => email.trim())
        .filter(email => email)
        .map(email => ({
          emailAddress: { address: email },
          type: 'required'
        }))
      
      const response = await fetch('https://graph.microsoft.com/v1.0/me/events', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: eventTitle,
          start: { dateTime: eventStart, timeZone: 'UTC' },
          end: { dateTime: eventEnd, timeZone: 'UTC' },
          attendees: attendeesList
        })
      })

      const duration = Date.now() - startTime
      const data = await response.json()
      
      if (response.ok) {
        updateTestResult(testId, {
          status: 'success',
          duration,
          response: { eventId: data.id, subject: data.subject, webLink: data.webLink }
        })
        toast.success('Calendar event created successfully!')
      } else {
        throw new Error(data.error?.message || 'Failed to create event')
      }
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message,
        response: null
      })
      toast.error(`Event creation failed: ${error.message}`)
    }
  }

  const testSearchCalendarEvents = async () => {
    const testId = addTestResult({
      operation: 'Search Calendar Events',
      status: 'pending',
      duration: 0,
      request: { query: calendarSearchQuery },
      response: null
    }).id

    const startTime = Date.now()
    
    try {
      const accessToken = await getValidAccessToken()
      
      const response = await fetch(
        `https://graph.microsoft.com/v1.0/me/events?$search="${calendarSearchQuery}"&$top=10&$select=subject,start,end,organizer,attendees`,
        {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        }
      )

      const duration = Date.now() - startTime
      const data = await response.json()
      
      if (response.ok) {
        updateTestResult(testId, {
          status: 'success',
          duration,
          response: { count: data.value?.length || 0, events: data.value }
        })
        toast.success(`Found ${data.value?.length || 0} calendar events`)
      } else {
        throw new Error(data.error?.message || 'Search failed')
      }
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message,
        response: null
      })
      toast.error(`Calendar search failed: ${error.message}`)
    }
  }

  const testListUpcomingEvents = async () => {
    const testId = addTestResult({
      operation: 'List Upcoming Events',
      status: 'pending',
      duration: 0,
      request: { startDate: new Date().toISOString() },
      response: null
    }).id

    const startTime = Date.now()
    
    try {
      const accessToken = await getValidAccessToken()
      const now = new Date().toISOString()
      const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString()
      
      const response = await fetch(
        `https://graph.microsoft.com/v1.0/me/calendarView?startDateTime=${now}&endDateTime=${nextWeek}&$select=subject,start,end,location,organizer&$orderby=start/dateTime`,
        {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        }
      )

      const duration = Date.now() - startTime
      const data = await response.json()
      
      if (response.ok) {
        updateTestResult(testId, {
          status: 'success',
          duration,
          response: { count: data.value?.length || 0, events: data.value }
        })
        toast.success(`Found ${data.value?.length || 0} upcoming events`)
      } else {
        throw new Error(data.error?.message || 'Failed to list events')
      }
    } catch (error: any) {
      updateTestResult(testId, {
        status: 'error',
        duration: Date.now() - startTime,
        error: error.message,
        response: null
      })
      toast.error(`List events failed: ${error.message}`)
    }
  }

  const clearResults = () => {
    setTestResults([])
    toast.info('Test results cleared')
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please sign in to test Graph API calls</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => blink.auth.login()} className="w-full">
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Live Graph API Testing</h1>
          <p className="text-muted-foreground mt-1">
            Test Microsoft Graph email and calendar operations with live API calls
          </p>
        </div>
        <Button onClick={clearResults} variant="outline" size="sm">
          <Trash2 className="h-4 w-4 mr-2" />
          Clear Results
        </Button>
      </div>

      <Tabs defaultValue="email" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="email">
            <Mail className="h-4 w-4 mr-2" />
            Email Operations
          </TabsTrigger>
          <TabsTrigger value="calendar">
            <Calendar className="h-4 w-4 mr-2" />
            Calendar Operations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="email" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Send Email */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Send className="h-5 w-5 mr-2" />
                  Send Email
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>To</Label>
                  <Input
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    placeholder="recipient@example.com"
                  />
                </div>
                <div>
                  <Label>Subject</Label>
                  <Input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Email subject"
                  />
                </div>
                <div>
                  <Label>Body</Label>
                  <Textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder="Email content"
                    rows={3}
                  />
                </div>
                <Button onClick={testSendEmail} className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Send Test Email
                </Button>
              </CardContent>
            </Card>

            {/* Search Emails */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Search className="h-5 w-5 mr-2" />
                  Search Emails
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Search Query</Label>
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="from:nicole subject:meeting"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Examples: from:email, subject:topic, body:keyword
                  </p>
                </div>
                <Button onClick={testSearchEmails} className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  Search Emails
                </Button>
              </CardContent>
            </Card>

            {/* Create Draft */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Create Draft
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Subject</Label>
                  <Input
                    value={draftSubject}
                    onChange={(e) => setDraftSubject(e.target.value)}
                    placeholder="Draft subject"
                  />
                </div>
                <div>
                  <Label>Body</Label>
                  <Textarea
                    value={draftBody}
                    onChange={(e) => setDraftBody(e.target.value)}
                    placeholder="Draft content"
                    rows={3}
                  />
                </div>
                <Button onClick={testCreateDraft} className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                  Create Draft
                </Button>
              </CardContent>
            </Card>

            {/* Move Email */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FolderOpen className="h-5 w-5 mr-2" />
                  Move Email
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Message ID</Label>
                  <Input
                    value={messageIdToMove}
                    onChange={(e) => setMessageIdToMove(e.target.value)}
                    placeholder="Enter message ID"
                  />
                </div>
                <div>
                  <Label>Target Folder</Label>
                  <Input
                    value={targetFolder}
                    onChange={(e) => setTargetFolder(e.target.value)}
                    placeholder="Archive, Deleted Items, etc."
                  />
                </div>
                <Button onClick={testMoveEmail} className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderOpen className="h-4 w-4 mr-2" />}
                  Move Email
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="calendar" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Create Event */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Create Event
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={eventTitle}
                    onChange={(e) => setEventTitle(e.target.value)}
                    placeholder="Event title"
                  />
                </div>
                <div>
                  <Label>Start Time</Label>
                  <Input
                    type="datetime-local"
                    value={eventStart}
                    onChange={(e) => setEventStart(e.target.value)}
                  />
                </div>
                <div>
                  <Label>End Time</Label>
                  <Input
                    type="datetime-local"
                    value={eventEnd}
                    onChange={(e) => setEventEnd(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Attendees</Label>
                  <Input
                    value={eventAttendees}
                    onChange={(e) => setEventAttendees(e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                  />
                </div>
                <Button onClick={testCreateCalendarEvent} className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calendar className="h-4 w-4 mr-2" />}
                  Create Event
                </Button>
              </CardContent>
            </Card>

            {/* Search Calendar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Search className="h-5 w-5 mr-2" />
                  Search Events
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Search Query</Label>
                  <Input
                    value={calendarSearchQuery}
                    onChange={(e) => setCalendarSearchQuery(e.target.value)}
                    placeholder="meeting, standup, review"
                  />
                </div>
                <Button onClick={testSearchCalendarEvents} className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  Search Calendar
                </Button>
              </CardContent>
            </Card>

            {/* List Upcoming */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Eye className="h-5 w-5 mr-2" />
                  Upcoming Events
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Retrieve all events scheduled for the next 7 days
                </p>
                <Button onClick={testListUpcomingEvents} className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                  List Upcoming Events
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Test Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <RefreshCw className="h-5 w-5 mr-2" />
            Live Test Results ({testResults.length})
          </CardTitle>
          <CardDescription>
            Real-time execution logs of all Graph API operations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <div className="space-y-3">
              {testResults.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No tests run yet. Click any test button above to begin.
                </div>
              ) : (
                testResults.map((result) => (
                  <Card key={result.id} className="border-l-4" style={{
                    borderLeftColor: result.status === 'success' ? '#22c55e' : result.status === 'error' ? '#ef4444' : '#f59e0b'
                  }}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {result.status === 'success' && <CheckCircle2 className="h-5 w-5 text-green-500" />}
                          {result.status === 'error' && <XCircle className="h-5 w-5 text-red-500" />}
                          {result.status === 'pending' && <Loader2 className="h-5 w-5 text-yellow-500 animate-spin" />}
                          <span className="font-semibold">{result.operation}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={result.status === 'success' ? 'default' : result.status === 'error' ? 'destructive' : 'secondary'}>
                            {result.status}
                          </Badge>
                          <span className="text-sm text-muted-foreground">{result.duration}ms</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="font-medium">Timestamp:</span>{' '}
                          <span className="text-muted-foreground">
                            {new Date(result.timestamp).toLocaleString()}
                          </span>
                        </div>
                        
                        <div>
                          <span className="font-medium">Request:</span>
                          <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-x-auto">
                            {JSON.stringify(result.request, null, 2)}
                          </pre>
                        </div>
                        
                        {result.response && (
                          <div>
                            <span className="font-medium">Response:</span>
                            <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-x-auto">
                              {JSON.stringify(result.response, null, 2)}
                            </pre>
                          </div>
                        )}
                        
                        {result.error && (
                          <div>
                            <span className="font-medium text-red-500">Error:</span>
                            <p className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-red-600">
                              {result.error}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  )
}
